import React, { useEffect, useState, useRef } from 'react';
import Highcharts from 'highcharts';
import classnames from 'classnames';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import { produce, setAutoFreeze } from 'immer';
import {
  defaultWaterfallChartOption,
  pointBreakColumn,
  shouldBreakColumn,
} from './waterfallConfig';
import { NoData } from '@components';
import D_Number from '@utils/number';
// import '@style/components/charts.scss';
import '../../../styles/components/charts.scss';
import useTheme from '@hooks/useTheme';
// import useDebounce from '@components/useDebounce';
HighchartsMore(Highcharts);
setAutoFreeze(false);
const BAR_COLORS = {
  EBITDA: { bgColor: 'rgba(243,204,98,0.9)', textColor: '#000' },
  NEG_EBITDA: { bgColor: 'rgba(224,48,30,0.8)' },
  COMBINED_EBITDA: { bgColor: 'rgba(255,182,0,0.9)', textColor: '#000' },
  SYNERGIES: {
    COST: { bgColor: '#69A3C9', textColor: '#DBDBDB' },
    REVENUE: { bgColor: 'rgba(38,155,238,0.9)' },
    CAPITAL_OPTIMIZATION: { bgColor: '#006CE2' },
    DIS_SYNERGY: { bgColor: 'rgba(224,48,30,0.8)', textColor: '#DBDBDB' },
  },
  COMBINED_SYNERGY: { bgColor: '#00398F', textColor: '#DBDBDB' },
};

const BAR_COLORS_OF_LIGHT_MODE = {
  EBITDA: { bgColor: '#FFC83D', textColor: '#000' },
  NEG_EBITDA: { bgColor: '#E0301E' },
  COMBINED_EBITDA: { bgColor: '#FFB600', textColor: '#000' },

  SYNERGIES: {
    COST: { bgColor: '#4DACF1', textColor: '#DBDBDB' },
    REVENUE: { bgColor: '#4DACF1' },
    CAPITAL_OPTIMIZATION: { bgColor: '#0089EB' },
    DIS_SYNERGY: { bgColor: '#E0301E', textColor: '#DBDBDB' },
  },
  COMBINED_SYNERGY: { bgColor: '#003DAB', textColor: '#DBDBDB' },
};

function isEmpty(value) {
  return value === null || value === undefined || value === 'TBD';
}

(function (HighCharts) {
  // this function need to call before chart load
  // Partial fix to show data labels position for css zoomed charts
  HighCharts.wrap(
    HighCharts.Pointer.prototype,
    'normalize',
    function (proceed, event, chartPosition) {
      const e = proceed.call(this, event, chartPosition);
      const element = this.chart.container;
      if (element && element.offsetHeight) {
        const scaleY =
          element.getBoundingClientRect().height / element.offsetHeight;
        if (e.chartY < 52) {
          e.chartY = parseInt(e.chartY / 0.4, 10);
        }
      }
      return e;
    },
  );
})(Highcharts);

const WaterfallChart = ({ dataSrc, isHidden, setIsHidden }) => {
  const chartRef = useRef(null);
  const isNoData = Object.keys(dataSrc || {}).length === 0;
  const [chartOption, setChartOption] = useState(null);
  const ebitLabel = dataSrc?.ebitLabel;
  const displayEbitAndDiligence = dataSrc?.displayEbitAndDiligence;
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  const COLORS = isLightMode ? BAR_COLORS_OF_LIGHT_MODE : BAR_COLORS;

  const fetchAndFormatData = (data) => {
    if (data.dataList.length === 0) {
      return;
    }
    const newOption = produce(defaultWaterfallChartOption, (draft) => {
      if (!data.dataList.filter((x) => x)) {
        return;
      }
      if (!displayEbitAndDiligence) {
        data.dataList = data.dataList.filter(
          (i) =>
            (!i.category?.includes(ebitLabel) &&
              i.category !== 'Diligence Adjustment') ||
            i.category?.includes('Combined Synergized'),
        );
      }
      const seriesData = [];
      data.dataList.forEach((item, index) => {
        const _seriesData = {
          name: item.category,
          y: item.value,
          others: {
            ...item,
            // mode: data.mode,
            format: data.format,
            displayFigures:
              data.displayFigures &&
              !isEmpty(item.financialImpactOfDollarValue),
          },
        };
        switch (item.category) {
          case `Buyer ${ebitLabel}`:
          case `Target ${ebitLabel}`:
          case 'Diligence Adjustment':
            _seriesData.color = COLORS.EBITDA.bgColor;
            _seriesData.textColor = COLORS.EBITDA.textColor;
            break;
          case `Combined ${ebitLabel}`:
            if (!item.value) {
              draft.series[0].lineColor = '#1d1d1d';
              break;
            }
            _seriesData.color = COLORS.COMBINED_EBITDA.bgColor;
            _seriesData.textColor = COLORS.COMBINED_EBITDA.textColor;
            _seriesData.isIntermediateSum = true;
            draft.series[0].lineColor = '#999999';
            break;
          case `Combined Synergized ${ebitLabel}`:
            if (!item.value) {
              break;
            }
            if (!displayEbitAndDiligence) {
              _seriesData.name = 'Total Synergies';
            }
            _seriesData.isSum = true;
            _seriesData.color = COLORS.COMBINED_SYNERGY.bgColor;
            _seriesData.textColor = COLORS.COMBINED_SYNERGY.textColor;
            break;
          default:
            switch (item.type) {
              case 'Dis-Synergy':
                _seriesData.color = COLORS.SYNERGIES.DIS_SYNERGY.bgColor;
                _seriesData.textColor = COLORS.SYNERGIES.DIS_SYNERGY.textColor;
                _seriesData.y = -_seriesData.y;
                break;
              default:
                switch (item.synergy) {
                  case 'Cost':
                    _seriesData.color = COLORS.SYNERGIES.COST.bgColor;
                    break;
                  case 'Capital Optimization':
                    _seriesData.color =
                      COLORS.SYNERGIES.CAPITAL_OPTIMIZATION.bgColor;
                    break;
                  case 'Revenue':
                    _seriesData.color = COLORS.SYNERGIES.REVENUE.bgColor;
                    break;
                  default:
                    _seriesData.color = COLORS.EBITDA.bgColor;
                    _seriesData.textColor = COLORS.EBITDA.textColor;
                  // return;
                }
            }
        }
        if (item.value < 0) {
          _seriesData.color = COLORS.NEG_EBITDA.bgColor;
        }
        seriesData.push(_seriesData);
        if (
          data.dataList[index].category === data.dataList[index + 1]?.category
        ) {
          // add null value in data series will remove dashed line between two columns
          seriesData.push({ name: item.category, y: null });
        }
      });
      draft.subtitle.text = `In ${data.format}`;
      draft.series[0].data = seriesData;
      draft.tooltip.formatter = null;
      const needBreak = shouldBreakColumn(seriesData, ebitLabel);
      if (
        data.dataList.filter((x) => x.category === `Combined ${ebitLabel}`)[0]
          ?.value &&
        needBreak
      ) {
        draft.yAxis.events = {
          pointBreak: pointBreakColumn,
        };
        draft.yAxis.breaks = [
          {
            from:
              data.dataList.filter(
                (x) => x.category === `Combined ${ebitLabel}`,
              )[0].value > 0
                ? data.dataList.filter(
                    (x) => x.category === `Combined ${ebitLabel}`,
                  )[0].value * 0.1
                : 0,
            to:
              data.dataList.filter(
                (x) => x.category === `Combined ${ebitLabel}`,
              )[0].value > 0
                ? data.dataList.filter(
                    (x) => x.category === `Combined ${ebitLabel}`,
                  )[0].value * 0.9
                : 0,
            breakSize: 0,
          },
        ];
      } else {
        delete draft.yAxis.breaks;
        delete draft.yAxis.events;
      }
      // adjust for light mode
      if (isLightMode) {
        // draft.series[0].borderWidth = 0;
        draft.series[0].borderColor = '#fff';
        draft.plotOptions.waterfall.dataLabels.style.fontWeight = 'normal';
        draft.plotOptions.waterfall.dataLabels.style.color = '#474747';
      }
      if (!data.displayFigures) {
        delete draft.plotOptions;
      }
    });
    setChartOption(newOption);
  };

  useEffect(() => {
    async function initData() {
      await fetchAndFormatData(dataSrc);
    }
    if (!isNoData) {
      initData();
      // console.log(ref.current.chart);
    }
  }, []);
  useEffect(() => {
    if (!chartOption || chartOption.tooltip.formatter) {
      return;
    }
    const tooltipFormatter = function () {
      const that = this;
      const modeText = `(In ${dataSrc?.format})`;
      const value = that.y == 0 ? 0 : D_Number.formatWithoutSuffix(that.y);
      const fiscalYearContent = that.point.others.fiscalYear
        ? `<div class='label'>Fiscal Year</div>
      <div class='content'>${that.point.others.fiscalYear}</div>`
        : '';

      // EBITAS
      if (!that.point.others.type) {
        if (that.key === 'Diligence Adjustment') {
          return `<div class='waterfall-tooltip${isLightMode ? '-light' : ''}' >
          <div class='title' style='background-color:${that.color};color:${
            that.point.textColor
          }'>${that.key}</div>
          <div class='label'>${that.key} ${modeText}</div>
          <div class='content'>${value}</div>`;
        }
        return `<div class='waterfall-tooltip${isLightMode ? '-light' : ''}' >
        <div class='title' style='background-color:${that.color};color:${
          that.point.textColor
        }'>${that.key}</div>
        <div class='label'>${that.key} ${modeText}</div>
        <div class='content'>${
          // that.point.others.mode === 'scale' &&
          that.point.others.category === `Combined Synergized ${ebitLabel}`
            ? ''
            : value
        }</div>
        ${fiscalYearContent}`;
      }
      // Synergies
      return `<div class='waterfall-tooltip${isLightMode ? '-light' : ''}' >
        <div class='title' style='background-color:${that.color};color:#fff'>${
        that.point.others.synergy
      } ${that.point.others.type}</div>
        <div class='label'>Probability of Success</div>
        <div class='content'>${that.point?.others.probabilityOfSuccess}</div>
        <div class='label'>Time to Achieve</div>
        <div class='content'>${that.point?.others.timeToAchieve}</div>
        <div class='label'>Financial Impact ${modeText}</div>
        <div class='content'>${
          that.point.others.financialImpactOfScale
            ? that.point.others.financialImpactOfScale
            : that.point.others.type === 'Dis-Synergy'
            ? D_Number.formatWithoutSuffix(
                that.point.others.financialImpactOfDollarValue,
                true,
              ) || 0
            : D_Number.formatWithoutSuffix(
                that.point.others.financialImpactOfDollarValue,
              ) || 0
        }</div>
        <div class='label'>One-time Cost to Achieve ${modeText}</div>
        <div class='content'>${
          typeof that.point.others.oneTimeCostToAchieveOfDollarValue ===
          'number'
            ? D_Number.formatWithoutSuffix(
                that.point.others.oneTimeCostToAchieveOfDollarValue,
                true,
              )
            : ['Low', 'Medium', 'High'].includes(
                that.point.others.oneTimeCostToAchieveOfScale,
              )
            ? that.point.others.oneTimeCostToAchieveOfScale
            : '-'
        }</div>
        </div>`;
    };
    const optionWithTooltip = produce(chartOption, (draft) => {
      draft.tooltip.formatter = tooltipFormatter;
    });
    setChartOption(optionWithTooltip);
  }, [chartOption]);
  // const updateSize = function (chartOption) {
  //   if (chartOption) {
  //     const newChartOption = produce(chartOption, (draft) => {
  //       draft.chart.width =
  //         window.innerWidth < 1080 ? 897 : window.innerWidth - 84 - 130;
  //     });
  //     setChartOption(newChartOption);
  //   }
  // };
  // const debouncedUpdateSize = useDebounce(updateSize, 500);
  //
  // useEffect(() => {
  //   window.addEventListener('resize', function (e) {
  //     debouncedUpdateSize(chartOption);
  //   });
  //   return () => {
  //     window.removeEventListener('resize', function (e) {
  //       debouncedUpdateSize(chartOption);
  //     });
  //   };
  // }, [chartOption]);

  useEffect(() => {
    if (!isHidden) {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize')); // force chart to recalculate size
      }, 0);
    }
  }, [isHidden]);

  return (
    <div
      className={classnames('waterfall-chart-wrapper', {
        'highcharts-dark': !isLightMode,
        'highcharts-light': isLightMode,
        'toggle-chart': isHidden,
      })}
    >
      {isNoData ? (
        <NoData />
      ) : (
        <>
          <div
            className={`waterfall-toggle-btn${isHidden ? ' toggled' : ''}`}
            onClick={() => {
              setIsHidden(!isHidden);
            }}
          >
            <span
              className={`appkiticon icon-${
                isHidden ? 'down' : 'up'
              }-chevron-fill`}
            />
          </div>
          {isHidden ? (
            <div className="waterfall-title-holder">Value Bridge</div>
          ) : (
            <HighchartsReact
              ref={chartRef}
              highcharts={Highcharts}
              options={chartOption}
              allowChartUpdate={true}
            />
          )}
        </>
      )}
    </div>
  );
};

export default WaterfallChart;
